import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';

const TWA_SESSION_KEY = 'isTwa';
const TWA_PARAM = 'isTwa';
// Chrome sets this referrer when the TWA opens a page, including deep links and notification taps
// that skip the `?isTwa=true` launch URL.
const TWA_REFERRER = 'android-app://com.c2fo.app.twa';

// sessionStorage throws when storage is blocked (e.g. disabled site data), and isTwaActive runs on
// every track event, so storage failures must never escape.
const readSessionFlag = (): boolean => {
  try {
    return sessionStorage.getItem(TWA_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeSessionFlag = (): void => {
  try {
    sessionStorage.setItem(TWA_SESSION_KEY, 'true');
  } catch {
    // The URL param still identifies the TWA on cold start; only in-app navigation loses the flag.
  }
};

/**
 * Returns true if the app is running inside the Android TWA wrapper. Checks the URL param and
 * the TWA referrer first, then falls back to sessionStorage so the flag survives in-app navigation.
 */
export function isTwaActive(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get(TWA_PARAM) === 'true' || document.referrer.startsWith(TWA_REFERRER) || readSessionFlag();
}

/**
 * Persists the isTwa flag in sessionStorage (the wrapper only launches with `?isTwa=true`
 * once, on cold start) and surfaces it as a DataDog RUM global context property.
 */
const useTrackTwaSession = (): void => {
  useEffect(() => {
    if (isTwaActive()) {
      writeSessionFlag();
      datadogRum.setGlobalContextProperty('isTwa', true);
    }
  }, []);
};

export default useTrackTwaSession;
